import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useSiteConfig } from "@/context/SiteConfigContext";

export default function ForgotPasswordPage() {
  const [username, setUsername] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const { resetPassword, isLoading } = useAuth();
  const { siteConfig } = useSiteConfig();

  const siteLogo = siteConfig?.logo || "https://public.readdy.ai/ai/img_res/1ebe5b90-bc3d-41ad-9f51-693742fb28f5.png";
  const siteDisplayName = siteConfig?.site_name || siteConfig?.name || "商服汇";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!username.trim()) {
      setError("请输入用户名");
      return;
    }

    const result = await resetPassword(username);
    if (result.success) {
      setSubmitted(true);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            {siteLogo && (
              <img
                src={siteLogo}
                alt={siteDisplayName}
                className="h-10 w-10 object-contain"
              />
            )}
            <span className="text-xl font-bold text-gray-800">{siteDisplayName}</span>
          </Link>
          <p className="text-gray-500 text-sm mt-2">找回密码</p>
        </div>

        {/* Form */}
        <div className="bg-white rounded-2xl p-6 md:p-8 border border-gray-100 shadow-sm">
          {submitted ? (
            <div className="text-center py-4">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-green-50">
                <i className="ri-mail-send-line text-2xl text-green-500" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                邮件已发送
              </h3>
              <p className="text-sm text-gray-500 mb-6">
                密码重置链接已发送至用户名「{username}」绑定的联系方式，请查收并按照提示操作。
              </p>
              <Link
                to="/login"
                className="inline-flex items-center px-5 py-2.5 bg-orange-500 text-white rounded-full hover:bg-orange-600 transition-colors text-sm"
              >
                返回登录
              </Link>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-500 mb-4">
                请输入您的用户名，我们将向绑定的联系方式发送密码重置链接。
              </p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    用户名
                  </label>
                  <div className="relative">
                    <i className="ri-user-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      placeholder="请输入用户名"
                      className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                    />
                  </div>
                </div>

                {error && (
                  <div className="p-3 bg-red-50 text-red-600 text-sm rounded-xl">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-3 bg-orange-500 text-white font-medium rounded-xl hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin" />
                      发送中...
                    </span>
                  ) : (
                    "发送重置链接"
                  )}
                </button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-sm text-gray-500">
                  想起密码了？{" "}
                  <Link
                    to="/login"
                    className="text-orange-600 font-medium hover:text-orange-700"
                  >
                    返回登录
                  </Link>
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
